import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Button,
  Typography,
  Box,
  Alert,
  Divider,
  Stack,
  useTheme
} from '@mui/material';
import { Google as GoogleIcon, AccountCircle as MicrosoftIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { signInWithGoogle, signInWithMicrosoft } from '../../firebase/auth';
import { captureUtmFromUrl, getStoredUtm, clearStoredUtm } from '../../utils/utmTracking';
import logo from '../../assets/logo.png';

const SignupForm = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const signupContextRef = useRef(null);

  // Capture URL UTM params on mount, then read merged UTM (URL + previously stored)
  // for attribution on signup. This persists last-touch across redirects.
  useEffect(() => {
    captureUtmFromUrl();
    const stored = getStoredUtm() || {};
    const planFromUrl = searchParams.get('plan');
    const utm = {
      utm_source: stored.utm_source,
      utm_medium: stored.utm_medium,
      utm_campaign: stored.utm_campaign,
      utm_content: stored.utm_content,
      utm_term: stored.utm_term,
    };
    const hasUtm = Object.values(utm).some((v) => !!v);
    if (!hasUtm && !planFromUrl && !stored.landingPath) {
      signupContextRef.current = null;
      return;
    }
    signupContextRef.current = {
      plan: planFromUrl || stored.plan || 'free',
      registrationContext: {
        source: 'oauth-signup',
        utm,
        landingPath: stored.landingPath,
        signupPath: window.location.pathname + window.location.search,
        capturedAt: stored.capturedAt,
      },
    };
  }, [searchParams]);

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setAuthError('');
    const { error } = await signInWithGoogle(signupContextRef.current);
    if (error) {
      setAuthError(error);
      setLoading(false);
    } else {
      clearStoredUtm();
      navigate('/dashboard');
    }
  };

  const handleMicrosoftSignIn = async () => {
    setLoading(true);
    setAuthError('');
    const { error } = await signInWithMicrosoft(signupContextRef.current);
    if (error) {
      setAuthError(error);
      setLoading(false);
    } else {
      clearStoredUtm();
      navigate('/dashboard');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#000000',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Paper 
            elevation={0} 
            sx={{ 
              p: { xs: 4, md: 6 },
              ...theme.customEffects?.glass,
              border: '1px solid rgba(255, 255, 255, 0.08)',
              borderRadius: 5,
              textAlign: 'center'
            }}
          >
            <Box sx={{ mb: 4 }}>
              <img 
                src={logo} 
                alt="FutureSense"
                style={{ height: '48px', width: 'auto', marginBottom: '24px' }}
              />
              <Typography 
                variant="h4" 
                sx={{
                  fontWeight: 800,
                  color: '#ffffff',
                  mb: 1,
                  letterSpacing: '-0.03em'
                }}
              >
                Join FutureSense.
              </Typography>
              <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                Start your AI-powered growth journey today.
              </Typography>
            </Box>
        
            {authError && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {authError}
              </Alert>
            )}

            <Stack spacing={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleGoogleSignIn}
                disabled={loading}
                startIcon={<GoogleIcon />}
                size="large"
                sx={{ 
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.03)'
                  }
                }}
              >
                Join with Google
              </Button>
              
              <Button
                fullWidth
                variant="outlined"
                onClick={handleMicrosoftSignIn}
                disabled={loading}
                startIcon={<MicrosoftIcon />}
                size="large"
                sx={{ 
                  py: 1.5,
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                  color: '#ffffff',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.03)'
                  }
                }}
              >
                Join with Microsoft
              </Button>
            </Stack>

            <Divider sx={{ my: 4, borderColor: 'rgba(255, 255, 255, 0.08)' }}>
              <Typography variant="caption" sx={{ color: theme.palette.text.disabled, px: 1 }}>
                ONE-CLICK REGISTRATION
              </Typography>
            </Divider>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                Already have an account?{' '}
                <Link to="/login" style={{ textDecoration: 'none', color: theme.palette.primary.light, fontWeight: 600 }}>
                  Sign In
                </Link>
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default SignupForm;